import React from 'react';
import { Polygon, Polyline } from '@react-google-maps/api';

import type { TenantRoute, TenantRoutePoint } from '@types';
import { routeColor } from '@utils/tenantRoutes';

/** `color` defaults to the route's position in the list */
export type ColoredRoute = TenantRoute & { color?: string };

type PropsType = {
    routes: ColoredRoute[],
    /** Drawn heavier and on top */
    highlightedId?: number | null,
    /** Fill/stroke of routes that are not highlighted (default: faint background routes) */
    fillOpacity?: number,
    strokeWeight?: number,
    /** Makes the routes clickable; the point clicked is passed on */
    onPointClick?: (point: TenantRoutePoint) => void,
    /** Dashed outline: Google's Polygon has no dashed stroke, so a Polyline is drawn over a polygon without edge */
    dashed?: boolean
};

/** Routes as non-editable polygons coloured with routeColor. */
export default function RoutePolygons(props: PropsType) {
    const {
        routes, highlightedId, fillOpacity = 0.12, strokeWeight = 1.5, onPointClick, dashed = false
    } = props;
    return (
        <>
            {routes.map((route, index) => {
                const color = route.color ?? routeColor(index);
                const highlighted = route.id === highlightedId;
                return (
                    <React.Fragment key={route.id}>
                        <Polygon
                            paths={route.points}
                            options={{
                                strokeColor: color,
                                fillColor: color,
                                fillOpacity: highlighted ? 0.28 : fillOpacity,
                                strokeWeight: highlighted ? 2.5 : strokeWeight,
                                // The edge is the Polyline when dashed
                                strokeOpacity: dashed ? 0 : 1,
                                clickable: !!onPointClick,
                                editable: false,
                                draggable: false,
                                zIndex: highlighted ? 2 : 1
                            }}
                            onClick={(e) => {
                                if (e.latLng) onPointClick?.({ lat: e.latLng.lat(), lng: e.latLng.lng() });
                            }}
                        />
                        {dashed && (
                            <Polyline
                                path={[...route.points, route.points[0]]}
                                options={{
                                    strokeColor: color,
                                    strokeOpacity: 0,
                                    clickable: false,
                                    zIndex: 2,
                                    icons: [{
                                        icon: {
                                            path: 'M 0,-1 0,1',
                                            strokeOpacity: 1,
                                            strokeWeight,
                                            scale: 3
                                        },
                                        offset: '0',
                                        repeat: '14px'
                                    }]
                                }}
                            />
                        )}
                    </React.Fragment>
                );
            })}
        </>
    );
}
